import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import I18n from 'utils/I18n';
import styles from './styles.scss';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import {
  cursorDataProps,
  indexProps,
  pageProps,
  feedTypeProps,
  viewingAthleteProps,
  commentsProps,
  timeAndLocationProps,
  highlightedKudosersProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';
import { buildInitialState } from '../../state/feed-entity-init-state';
import CommentDeleteAlertDialog from '../shared/CommentDeleteAlertDialog';
import EntryHeader from '../shared/EntryHeader/EntryHeader';
import videoIcon from './video-icon.png';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import KudosAndComments from '../shared/KudosAndComments';
import { getPhotoWithMaxDimension } from '../../utils';

const Post = ({
  viewingAthlete,
  clubId,
  post,
  page,
  entity,
  feedType,
  index,
  cursorData,
  timeAndLocation,
  body,
  sharedContents,
  athleteProfileId
}) => {
  const analyticProps = {
    page,
    entity,
    entityId: post.entity_id,
    feedType,
    index,
    athleteProfileId,
    viewingAthleteId: viewingAthlete.id,
    clubId
  };
  const { trackAnalytics, intersectionObserverRef } = useFeedAnalytics({
    ...analyticProps
  });

  const cardMargin = index > 0 ? styles.cardMargin : '';
  const [entityState, dispatch] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics,
      comments: { [post.entity_id]: post.comments }
    }),
    init
  );
  const providerState = {
    entityState,
    dispatch,
    analyticProps
  };

  const profileUrl =
    post.path_prefix && post.path_prefix.concat(post.originator_id);

  const entityOwner = {
    displayImage: post.originator_avatar_url,
    memberType: post.member_type,
    name: post.originator_name,
    profileUrl,
    type: post.parent_type.toLowerCase()
  };

  const entryExpandedLink = () => {
    const videoThumbnailIcon = I18n.t(
      'templates.feed.posts.video_thumbnail_icon'
    );
    return sharedContents.map((content) => {
      // eg. expected scss class : shared-video is referenced as sharedVideo in JSX
      const typeClass = `shared${content.type.charAt(0).toUpperCase() +
        content.type.slice(1)}`;
      return (
        <a
          className={styles.sharedContentContainer}
          target="_blank"
          rel="noopener noreferrer"
          href={content.url}
          key={content.id}
          data-testid="shared-content"
          onClick={() =>
            trackAnalytics({
              action: 'click',
              element: 'expanded_link'
            })
          }
        >
          <div className={`${styles.sharedContent} mt-sm ${styles[typeClass]}`}>
            {/*  thumbnail image  */}
            {content.thumbnail_url && (
              <div className={styles.sharedContentImage}>
                {content.type === 'video' && (
                  <img
                    className={styles.sharedContentVideoIcon}
                    src={videoIcon}
                    alt={videoThumbnailIcon}
                  />
                )}
                <img
                  className={styles.sharedContentThumbnail}
                  src={content.thumbnail_url}
                  alt=""
                />
              </div>
            )}
            {/*  description  */}
            <div className={styles.sharedContentText}>
              <h4 className={styles.sharedContentHeader}>{content.title}</h4>
              <p className={`${styles.sharedContentDescription} mt-sm mb-sm`}>
                {content.description}
              </p>
              <p className={styles.sharedContentDomain}>{content.hostname}</p>
            </div>
          </div>
        </a>
      );
    });
  };

  const buildEntryBody = () => {
    let bodyStyle = [styles.postBody];
    if (!post.summary) {
      bodyStyle.push(styles.postBodyTitle);
    }
    bodyStyle = bodyStyle.join(' ');
    return (
      <>
        {/* Summary & Body */}
        <div className={`${styles.entryBody} ${styles.entryInset}`}>
          <a
            href={`${profileUrl}/posts/${post.entity_id}`}
            onClick={() =>
              trackAnalytics({
                action: 'click',
                element: 'body'
              })
            }
          >
            {post.summary && (
              <h3 className={styles.entryTitle}>
                <Markup noWrap={true} content={post.summary} />
              </h3>
            )}
            <div className={bodyStyle}>
              <Markup noWrap={true} content={body} />
            </div>
          </a>
        </div>
        {/* Expanded Link */}
        <div className={`${styles.entryMedia} ${styles.entryInset}`}>
          {entryExpandedLink()}
        </div>
      </>
    );
  };

  const highlightPhoto = () => {
    const photoUrlHash = post.primary_photo.urls;
    // max dimension image url
    const photoUrl = getPhotoWithMaxDimension(photoUrlHash);
    return {
      large: photoUrl,
      caption_escaped: post.primary_photo.caption,
      photo_id: post.primary_photo.unique_id
    };
  };

  return (
    <FeedEntityContext.Provider value={providerState}>
      {/* react-feed-entry is being used by feed3 container for pagination - https://github.com/strava/active/blob/main/app/assets/javascripts/strava/dashboard/PaginationView.js.coffee#L52-L73 */}
      <div
        className={`react-feed-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
        data-updated-at={cursorData.updated_at}
        data-rank={cursorData.rank}
        data-testid="web-feed-entry"
        ref={intersectionObserverRef}
      >
        {/* Entry Header - avatar, location, social actions */}
        <EntryHeader
          timeAndLocation={timeAndLocation}
          entityOwner={entityOwner}
          entityId={post.entity_id && post.entity_id.toString()}
          isBoosted={post.is_boosted || false}
          shareable={post.is_sharable || false}
          twitterUrl={post.twitter_url}
        />
        {/* Summary, Body and Expandanded Links */}
        {buildEntryBody()}

        {/* Primary Photo */}
        {post.primary_photo && (
          <div className={styles.entryMedia}>
            <a
              href={`${profileUrl}/posts/${post.entity_id}`}
              onClick={() =>
                trackAnalytics({
                  action: 'click',
                  element: 'image'
                })
              }
            >
              <PhotosAndMapImage
                entryType={entity.toLowerCase()}
                photoList={[highlightPhoto()]}
                feedName={page}
              />
            </a>
          </div>
        )}
        {/* Comments & Kudos */}
        <div className={styles.entryFooter}>
          <KudosAndComments
            kudosAndComments={{
              canKudo: post.can_kudo,
              kudosCount: post.kudos_count,
              highlightedKudosers: post.highlighted_kudosers,
              commentsEnabled: post.comments_enabled,
              comments: entityState.comments[post.entity_id] || []
            }}
            entity={entity}
            entityId={post.entity_id.toString()}
            ownedByCurrentAthlete={
              viewingAthlete.id.toString() === post.originator_id.toString()
            }
            viewingAthlete={viewingAthlete}
            entityOwner={entityOwner}
            entityTitle={post.summary || ''}
            openCommentsModalOnIconClick={false}
            showHairlineOnTop={false}
          />
        </div>
        <CommentDeleteAlertDialog />
      </div>
    </FeedEntityContext.Provider>
  );
};

Post.propTypes = {
  // feed entry types could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Post']).isRequired,
  feedType: feedTypeProps.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  index: indexProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  post: PropTypes.shape({
    entity_id: PropTypes.number.isRequired,
    comments: commentsProps,
    originator_id: PropTypes.number.isRequired,
    originator_avatar_url: PropTypes.string,
    originator_name: PropTypes.string,
    parent_type: PropTypes.string,
    member_type: PropTypes.string,
    summary: PropTypes.string,
    is_boosted: PropTypes.bool,
    is_sharable: PropTypes.bool,
    twitter_url: PropTypes.string,
    path_prefix: PropTypes.string,
    primary_photo: PropTypes.shape({
      urls: PropTypes.shape({}),
      large: PropTypes.string,
      caption: PropTypes.string,
      unique_id: PropTypes.string
    }),
    can_kudo: PropTypes.bool,
    kudos_count: PropTypes.number,
    highlighted_kudosers: highlightedKudosersProps,
    comments_enabled: PropTypes.bool
  }).isRequired,
  body: PropTypes.string.isRequired,
  sharedContents: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      post_id: PropTypes.number,
      thumbnail_url: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      url: PropTypes.string,
      hostname: PropTypes.string
    })
  ),
  timeAndLocation: timeAndLocationProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired
};

Post.defaultProps = {
  sharedContents: []
};

export default Post;
